import type {
  AxisAssessment,
  CriticalSignal,
  LevelAssessmentResult,
  SectionResult,
  WhyPoint,
} from "@shared/labs/level-assessment";
import {
  type AxisLevelScore,
  aggregateByMeanDispersion,
  combineOverall,
} from "@shared/labs/level-assessment-mean-aggregation";
import type { Discipline, ModelAxis, ModelSnapshot } from "@shared/labs/level-assessment-model";
import type { ExtractedProfileViewModel } from "./results/extracted-profile-panel";

/**
 * Demo fixtures for the level-assessment UI shell. Used by:
 *   - the form's "ver demo" affordance,
 *   - URL query `?demo=<key>` for design/dev review and Playwright.
 *
 * Each fixture exercises a different branch: cross-only clean, cross-only
 * abstention, and a cross + vertical (discipline) run. Fixtures pass through
 * the same aggregator + overall combiner the backend uses, so the demos
 * render exactly what production would for the same axis levels.
 */

const CROSS_AXES: ModelAxis[] = [
  { id: "influence", label: "Influence", description: "", weight: 1 },
  { id: "autonomy", label: "Autonomy", description: "", weight: 1 },
  { id: "activity_complexity", label: "Activity Complexity", description: "", weight: 1 },
  { id: "responsibility", label: "Responsibility", description: "", weight: 1 },
];
const LEVELS = { min: 0, max: 7 };

/** A demo Engineering discipline with two vertical competencies. */
const ENG_DISCIPLINE: Discipline = {
  id: "engenharia",
  label: "Engenharia",
  description: "Competências técnicas verticais de engenharia.",
  order: 0,
  axes: [
    { id: "programacao", label: "Programação", description: "", weight: 1 },
    { id: "arquitetura", label: "Arquitetura de sistemas", description: "", weight: 1 },
  ],
};

type AxisInput = {
  axis: string;
  inferredLevel: number | null;
  confidence: number;
  reasoning: string;
  ruleApplied?: string;
  whyThisLevel?: WhyPoint[];
  whyNotHigher?: WhyPoint[];
  whyNotLower?: WhyPoint[];
  missingEvidence?: string[];
  /** Explainability layer (demo): the rubric line for the inferred level. */
  levelRubric?: string;
  /** Explainability layer (demo): critical signals + profile citations. */
  criticalSignals?: CriticalSignal[];
};

function toAxisAssessments(scope: "cross" | "vertical", inputs: AxisInput[]): AxisAssessment[] {
  return inputs.map((a) => {
    const abstained = a.inferredLevel === null;
    return {
      axis: a.axis,
      scope,
      inferredLevel: a.inferredLevel,
      confidence: a.confidence,
      reasoning: a.reasoning,
      evidence: [],
      ruleApplied: a.ruleApplied ?? "",
      // Default the satisfied column to the one-line reasoning so the panel
      // always has something to show; abstained axes get a missing-evidence row.
      whyThisLevel:
        a.whyThisLevel ??
        (abstained ? [] : [{ point: a.reasoning, evidence: "Evidência do perfil." }]),
      whyNotHigher: a.whyNotHigher ?? [],
      whyNotLower: a.whyNotLower ?? [],
      missingEvidence: a.missingEvidence ?? (abstained ? [a.reasoning] : []),
      levelRubric: a.levelRubric,
      criticalSignals: a.criticalSignals ?? [],
    };
  });
}

function toScores(inputs: AxisInput[]): AxisLevelScore[] {
  return inputs.map((a) => ({
    axis: a.axis,
    level: a.inferredLevel,
    confidence: a.confidence,
    reasoning: a.reasoning,
  }));
}

function buildSection(scope: "cross" | "vertical", axes: ModelAxis[], inputs: AxisInput[]) {
  const agg = aggregateByMeanDispersion({
    axes: axes.map((a) => ({ id: a.id, label: a.label, weight: a.weight })),
    levels: LEVELS,
    scores: toScores(inputs),
  });
  const section: SectionResult = {
    axes: toAxisAssessments(scope, inputs),
    finalLevel: agg.finalLevel,
    confidence: agg.reliability,
    mean: agg.mean,
    stddev: agg.stddev,
    abstainedAxes: agg.abstainedAxes,
    rationale: agg.rationale,
    globalNotes:
      agg.mean !== null && agg.stddev !== null
        ? `Mean ${agg.mean.toFixed(2)} · stddev ${agg.stddev.toFixed(2)} · reliability ${agg.reliability.toFixed(2)}.`
        : undefined,
  };
  return { section, agg };
}

interface DemoFixture {
  candidateName: string;
  contextLabel: string;
  cross: AxisInput[];
  /** When set, the demo run targets this discipline (vertical section). */
  discipline?: { def: Discipline; axes: AxisInput[] };
  /** Candidate narrative the explainability citations quote from. When present,
   *  `getDemoFixture` returns an `extractedProfile` so the "Ver no perfil"
   *  highlight works end-to-end in the demo (no LLM). */
  narrative?: string;
}

/** Demo narrative for the senior fixture. The explainability citation quotes
 *  below are verbatim substrings of this text so the highlight resolves. */
const SENIOR_NARRATIVE = `Camila Ribeiro
Headline: Líder de iniciativas cross-funcionais · Produto & Dados

Resumo:
Conduzo alinhamentos entre times sem mandato formal de gestão, conectando produto, dados e operações para destravar iniciativas de área.

Experiência:
Empresa: Nubank
Cargo: Staff Product Analyst (2021–2024)
Liderou iniciativa cross entre produto, dados e operações, alinhando três áreas em torno de um north-star comum. Navegou ambiguidade sem direção próxima.

Empresa: iFood
Cargo: Senior Analyst (2018–2021)
Entregou projetos de dados com autonomia, definindo escopo a partir de OKRs amplos.`;

export type DemoKey = "junior-cross" | "senior-cross" | "abstention" | "eng-discipline";

const FIXTURES: Record<DemoKey, DemoFixture> = {
  "junior-cross": {
    candidateName: "Ana Silva · Demo Júnior",
    contextLabel: "Comp Universal",
    cross: [
      {
        axis: "influence",
        inferredLevel: 2,
        confidence: 0.82,
        reasoning: "Ownership de features individuais, sem impacto cross-time.",
      },
      {
        axis: "autonomy",
        inferredLevel: 2,
        confidence: 0.78,
        reasoning: "Executa projetos definidos pelo TL.",
      },
      {
        axis: "activity_complexity",
        inferredLevel: 2,
        confidence: 0.8,
        reasoning: "Fluência na stack, sem design de sistema.",
      },
      {
        axis: "responsibility",
        inferredLevel: 1,
        confidence: 0.75,
        reasoning: "Sem menção a mentoria.",
      },
    ],
  },
  "senior-cross": {
    candidateName: "Camila Ribeiro · Demo Sênior",
    contextLabel: "Comp Universal",
    narrative: SENIOR_NARRATIVE,
    cross: [
      {
        axis: "influence",
        inferredLevel: 5,
        confidence: 0.88,
        reasoning: "Impacto cross-time em iniciativa de área.",
        ruleApplied:
          "Influence ancora no alcance da persuasão; liderar iniciativas cross sem mandato formal satisfaz o piso de L5.",
        levelRubric:
          "Impacto e persuasão atravessam a própria área; lidera iniciativas cross sem mandato formal.",
        criticalSignals: [
          {
            key: "sm_llm_has_executive_stakeholder_influence",
            value: true,
            valueLabel: "Sim",
            title: "Influência com C-suite",
            kind: "yes",
            strength: "strong",
            rubricConnection:
              "Alinhar três áreas em torno de um north-star comum, sem cargo de gestão, sustenta o piso de L5 em influência.",
            evidence: [
              {
                role: "Staff Product Analyst — Nubank",
                meta: "2021–2024",
                quote: "Liderou iniciativa cross entre produto, dados e operações",
              },
            ],
          },
          {
            key: "sm_llm_has_cross_functional_scope",
            value: true,
            valueLabel: "Sim",
            title: "Liderança cross-funcional",
            kind: "yes",
            strength: "moderate",
            rubricConnection:
              "Conduzir alinhamentos entre times sem mandato formal indica persuasão por alinhamento, não por autoridade.",
            evidence: [
              {
                role: "Resumo do perfil",
                meta: "",
                quote: "Conduzo alinhamentos entre times sem mandato formal de gestão",
              },
            ],
          },
        ],
        whyThisLevel: [
          {
            point: "Persuade fora da própria área.",
            evidence: "Iniciativas entre produto, dados e operações.",
          },
          {
            point: "Resultado por alinhamento, não por mando.",
            evidence: "Sem cargo de gestão declarado.",
          },
        ],
        whyNotHigher: [
          {
            point: "Sem influência em nível de organização.",
            evidence: "Nenhum sinal de mudar a direção de uma área inteira.",
          },
        ],
        whyNotLower: [
          {
            point: "Acima de L4: ultrapassa a fronteira do squad.",
            evidence: "Influência documentada entre áreas.",
          },
        ],
      },
      {
        axis: "autonomy",
        inferredLevel: 5,
        confidence: 0.86,
        reasoning: "Define direção de sub-área sob OKRs amplos.",
        ruleApplied:
          "Autonomy mede o grau de direção necessária; escopo crescente em org matricial indica L5.",
        levelRubric:
          "Define direção de sub-área sob OKRs amplos; navega ambiguidade sem direção próxima.",
        criticalSignals: [
          {
            key: "sm_llm_navigates_ambiguity",
            value: true,
            valueLabel: "Sim",
            title: "Navega ambiguidade sem direção",
            kind: "yes",
            strength: "strong",
            rubricConnection:
              "Navegar ambiguidade sem direção próxima é exatamente o piso de autonomia descrito em L5.",
            evidence: [
              {
                role: "Staff Product Analyst — Nubank",
                meta: "2021–2024",
                quote: "Navegou ambiguidade sem direção próxima",
              },
            ],
          },
        ],
        whyThisLevel: [
          {
            point: "Navega ambiguidade sem direção próxima.",
            evidence: "Iniciativas cross em ambiente matricial.",
          },
        ],
        whyNotHigher: [
          {
            point: "Não define a agenda de uma área.",
            evidence: "Atua dentro de objetivos dados, não os define.",
          },
        ],
        whyNotLower: [
          {
            point: "Acima de L4: não precisa de escopo pré-mastigado.",
            evidence: "Lida com problemas abertos.",
          },
        ],
      },
      {
        axis: "activity_complexity",
        inferredLevel: 5,
        confidence: 0.87,
        reasoning: "Lida com ambiguidade técnica; define padrões.",
      },
      {
        axis: "responsibility",
        inferredLevel: 5,
        confidence: 0.84,
        reasoning: "Tech lead formal de squad.",
      },
    ],
  },
  abstention: {
    candidateName: "Fernanda Lima · Demo Sinal Insuficiente",
    contextLabel: "Comp Universal",
    cross: [
      {
        axis: "influence",
        inferredLevel: 3,
        confidence: 0.6,
        reasoning: "Sinal moderado de subsistema-nível.",
      },
      {
        axis: "autonomy",
        inferredLevel: null,
        confidence: 0.3,
        reasoning: "Perfil sem descrição de roles.",
      },
      {
        axis: "activity_complexity",
        inferredLevel: 3,
        confidence: 0.55,
        reasoning: "Stack identificável, profundidade incerta.",
      },
      {
        axis: "responsibility",
        inferredLevel: null,
        confidence: 0.25,
        reasoning: "Zero menção a liderança.",
      },
    ],
  },
  "eng-discipline": {
    candidateName: "Daniel Marques · Demo Engenharia",
    contextLabel: "Engenharia",
    cross: [
      {
        axis: "influence",
        inferredLevel: 5,
        confidence: 0.88,
        reasoning: "Impacto cross-time consistente.",
      },
      {
        axis: "autonomy",
        inferredLevel: 5,
        confidence: 0.85,
        reasoning: "Define direção sob north-star.",
      },
      {
        axis: "activity_complexity",
        inferredLevel: 6,
        confidence: 0.89,
        reasoning: "Re-significa problemas difíceis de enunciar.",
      },
      {
        axis: "responsibility",
        inferredLevel: 5,
        confidence: 0.84,
        reasoning: "Lidera squad técnico.",
      },
    ],
    discipline: {
      def: ENG_DISCIPLINE,
      axes: [
        {
          axis: "programacao",
          inferredLevel: 6,
          confidence: 0.9,
          reasoning: "Profundidade técnica excepcional; contribuições de referência.",
        },
        {
          axis: "arquitetura",
          inferredLevel: 5,
          confidence: 0.82,
          reasoning: "Desenhou sistemas distribuídos em produção.",
        },
      ],
    },
  },
};

export function getDemoFixture(key: DemoKey): {
  candidateName: string;
  contextLabel: string;
  result: LevelAssessmentResult;
  extractedProfile?: ExtractedProfileViewModel;
} {
  const fixture = FIXTURES[key];
  const cross = buildSection("cross", CROSS_AXES, fixture.cross);
  const vertical = fixture.discipline
    ? buildSection("vertical", fixture.discipline.def.axes, fixture.discipline.axes)
    : buildSection("vertical", [], []);
  // Weighted overall across ALL run axes (cross + vertical) — same call the
  // backend orchestrator makes; sections are informational.
  const runAxes = [...CROSS_AXES, ...(fixture.discipline?.def.axes ?? [])];
  const overall = combineOverall({
    axes: runAxes.map((a) => ({ id: a.id, label: a.label, weight: a.weight })),
    levels: LEVELS,
    scores: [...toScores(fixture.cross), ...toScores(fixture.discipline?.axes ?? [])],
  });

  const modelSnapshot: ModelSnapshot = {
    id: "comp-universal",
    name: "Comp Universal",
    crossAxes: CROSS_AXES,
    discipline: fixture.discipline?.def ?? null,
    levels: LEVELS,
  };

  const result: LevelAssessmentResult = {
    cross: cross.section,
    vertical: vertical.section,
    overallLevel: overall.overallLevel,
    overallConfidence: overall.overallConfidence,
    overallMean: overall.overallMean,
    overallStddev: overall.overallStddev,
    overallRationale: overall.overallRationale,
    aggregationRule: "weighted-mean+dispersion",
    modelSnapshot,
  };

  const extractedProfile: ExtractedProfileViewModel | undefined = fixture.narrative
    ? {
        narrative: fixture.narrative,
        stats: {
          positionsCount: 2,
          educationsCount: 0,
          positionsWithDescription: 2,
          skillsCount: 0,
          hasSummary: true,
        },
        source: "linkedin",
      }
    : undefined;

  return {
    candidateName: fixture.candidateName,
    contextLabel: fixture.contextLabel,
    result,
    extractedProfile,
  };
}

export const DEMO_KEYS: { key: DemoKey; label: string }[] = [
  { key: "junior-cross", label: "Júnior · cross-only" },
  { key: "senior-cross", label: "Sênior · cross-only" },
  { key: "abstention", label: "Sinal insuficiente" },
  { key: "eng-discipline", label: "Engenharia · cross + vertical" },
];
